import type * as Security from '@ni/shared';
import type * as Reports from '@ni/shared';
import type * as Events from '@ni/shared';
import { OFFICE_WRITE_ROUTES, type OfficeWriteRoute, type WriteAxis } from '@ni/shared';
import type {
  AnswerDecisionBody,
  AnswerDecisionResponse,
  MergeTriageUndoResponse,
  UnmergeResult,
  BulkRowsBody,
  BulkRowsResponse,
  CommitImportBody,
  CorrectFactBody,
  CorrectFactResponse,
  CreateInputBody,
  EditInputBody,
  CreateRunBody,
  CreateRunResponse,
  ConnectorView,
  CreditSummary,
  RunCreditPrice,
  EventScanCreditPrice,
  ImportCreditPrice,
  CreateOrganisationBody,
  CreateOrganisationResponse,
  CreateSubjectBody,
  CreateSubjectResponse,
  DecisionDetail,
  DecisionLogResponse,
  DeleteSubjectResponse,
  ExtractRetryResponse,
  FactClaimsResponse,
  ImportPlan,
  ImportPlanVersion,
  ImportRow,
  ImportRowsResponse,
  ImportSession,
  ImportSessionListEntry,
  ImportTurnResponse,
  InputExtractionResult,
  KnowledgeGraph,
  KnowledgeJoinBody,
  KnowledgeJoinKeep,
  KnowledgeJoinPreview,
  KnowledgeJoinResponse,
  KnowledgeKind,
  KnowledgeFamilyLinkBody,
  KnowledgeLinkBody,
  KnowledgeLinkResponse,
  KnowledgeRenameBody,
  KnowledgeRenameResponse,
  KnowledgePanel,
  KnowledgeSearchResponse,
  ListImportRowsQueryInput,
  MeResponse,
  NeedsActionResponse,
  NetworkResponse,
  OrganisationListResponse,
  OrganisationRecord,
  OrganisationStatementBody,
  OrganisationStatementResponse,
  OrgSettingsUpdate,
  OrgSettingsView,
  ReadImportNotesBody,
  ReclassifyInteractionResponse,
  RemoveInputResponse,
  ResumeRunBody,
  ResumeRunResponse,
  CancelRunResponse,
  RetryImportRowsResponse,
  RunDetail,
  SendImportMessageBody,
  SetRowBody,
  SetSubjectStatusBody,
  SetSubjectStatusResponse,
  ProfileLinkAction,
  ProfileLinkResponse,
  RunFindingsResponse,
  RunListResponse,
  SubjectBrief,
  SubjectData,
  SubjectListResponse,
  SubjectListLevel,
  SubjectListSort,
  SubjectResearchStatus,
  SubjectRecord,
  UntrackOrganisationResponse,
  UnwindImportResponse,
  WrongPersonResponse,
} from '@ni/shared';
import { DASHBOARD_BUILD_HEADER, noteServerBuild } from '../lib/app-build';
import { toApiError } from './errors';

/**
 * The typed API client. Every shape is `@ni/shared`'s — nothing is redefined
 * here, which is the whole point of the contracts package (ui-design §6.1).
 *
 * Paths: every dashboard route is org-scoped under `/orgs/:orgId/…` with
 * `:orgId` the org **uuid** (server-design §3); `GET /me` is the one org-less
 * bootstrap read.
 */

export type AuthedFetch = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * Which dossier the research page is asking the brief for (§3e option b).
 *
 * Deliberately not `BriefQuery['view']` (`'draft' | undefined`): this value is
 * also a query-key element, and a key that reads `undefined` is a key that
 * cannot be told apart from a missing argument. `auto` names the default out
 * loud, and the client turns it back into an absent parameter on the wire.
 */
export type BriefViewRequest = 'auto' | 'draft';

/**
 * What `GET /subjects` is asked for.
 *
 * Written out rather than taken as `ListSubjectsQueryInput`, and for one
 * reason: `status` is a repeatable parameter, so on the schema's *input* side it
 * is `unknown` — the preprocess that turns `?status=member` into an array of one
 * accepts whatever the wire hands it. `unknown` is the right type for a server
 * parsing a URL and the wrong one for a caller building it; here it is an array
 * of words, and saying so is what makes the repeat below type-check.
 *
 * The two enums *are* the shared ones. Nothing about the vocabulary is
 * restated — only the shape of the call.
 */
export interface SubjectListParams {
  q?: string;
  limit?: number;
  cursor?: string;
  /** The chosen status words. Absent means every status the vertical has. */
  status?: readonly string[];
  sort?: SubjectListSort;
  research_status?: SubjectResearchStatus;
  level?: SubjectListLevel;
  /** One import session's own people — what `/people?import=<id>` asks for. */
  import_id?: string;
  /**
   * **One protection account's own people** — what `/people?account_id=<id>`
   * asks for, and what the account page's roster is. Refused by a vertical that
   * has no accounts, so it is only ever sent by a security org's own chip.
   */
  account_id?: string;
}

/**
 * What `GET /knowledge` is asked for. `kind` is repeatable, like the People
 * list's `status`, and for the same reason: the chips are a multi-select.
 *
 * `limit` is deliberately absent. Fifty is the server's default *and* its
 * maximum, and it is the number the page is designed around — past it the page
 * says to narrow the search rather than offering a page two — so there is
 * nothing here for a caller to choose.
 */
export interface KnowledgeSearchParams {
  account_id?: string;
  q?: string;
  /** The chosen kinds. Absent means all three. */
  kind?: readonly KnowledgeKind[];
}

/**
 * What `GET /organisations` is asked for (organisations-as-subjects note §1 R8).
 *
 * One control and a cursor, which is the whole of the list: it is **tracked
 * only** and that is not a filter — an organisation the office does not keep a
 * record of is found in the knowledge base, which is the every-kind surface —
 * and there is no status to chip and no order to choose, because an organisation
 * has neither.
 */
/** What `GET /events` is asked for: the organisation list's parameters. */
export interface EventListParams {
  q?: string;
  cursor?: string;
  limit?: number;
  account_id?: string;
}

/** The Attendees read's filters (`ListEventAttendeesQuery`, less paging). */
export interface EventAttendeesParams {
  q?: string;
  status?: Events.EventAttendeeStatus;
  review?: 'true';
  frame?: string;
  tier?: string;
}

export interface OrganisationListParams {
  q?: string;
  limit?: number;
  cursor?: string;
  /** One protection account's own organisations — the People list's parameter. */
  account_id?: string;
}

/** What `GET /imports/:id/rows` is asked for — the People tab's filter, paged. */
export type ImportRowParams = Omit<ListImportRowsQueryInput, 'limit'> & { limit?: number };

async function request<T>(
  fetchWithAuth: AuthedFetch,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('accept', 'application/json');
  if (init?.body !== undefined) headers.set('content-type', 'application/json');

  const response = await fetchWithAuth(path, { ...init, headers });
  noteServerBuild(response.headers.get(DASHBOARD_BUILD_HEADER));
  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * A query string, with an array value **repeated** rather than joined: the
 * People screen's status chips are a multi-select, and `?status=member&status=contact`
 * is how the list route's schema reads more than one (`shared/src/subjects.ts`).
 * A comma-joined single value would arrive as one word nobody's vertical has.
 *
 * An empty array is an absent parameter, not `?status=` — nothing chosen means
 * every status, which is what sending nothing says.
 */
function query(params: Record<string, string | number | readonly string[] | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      for (const one of value) search.append(key, one);
    } else {
      search.set(key, String(value));
    }
  }
  const rendered = search.toString();
  return rendered.length > 0 ? `?${rendered}` : '';
}

/**
 * The file name a `Content-Disposition` gives: the full `filename*` (RFC 6266)
 * when it is there, else the plain `filename`.
 */
export function dispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const extended = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1]);
    } catch {
      // A malformed encoding falls back to the plain name.
    }
  }
  return /filename="([^"]+)"/i.exec(header)?.[1] ?? null;
}

/** The org-less bootstrap read: the caller and their orgs. */
export function fetchMe(fetchWithAuth: AuthedFetch): Promise<MeResponse> {
  return request<MeResponse>(fetchWithAuth, '/me');
}

export interface Api {
  demoManifest(): Promise<import('../routes/demo').DemoManifestView>;
  demoEvidence(id: string): Promise<{ text: string; captured_at: string; sha256: string }>;
  /**
   * **The protection accounts API**, which is four calls (security fold,
   * 2026-09-22). The roster, the directory, the account-scoped knowledge search
   * and the context-change inbox were parallel surfaces over a parallel
   * substrate; they are the standard reads with `account_id` on them now, and
   * what is left here is the engagement itself.
   */
  securityAccounts(
    state?: 'active' | 'archived' | 'all',
    offset?: number,
    q?: string,
  ): Promise<Security.SecurityAccountListResponse>;
  createSecurityAccount(
    body: Security.CreateSecurityAccountBody,
  ): Promise<Security.SecurityAccountResponse>;
  securityAccount(id: string): Promise<Security.SecurityAccountDetailResponse>;
  updateSecurityAccount(
    id: string,
    body: Security.UpdateSecurityAccountBody,
  ): Promise<Security.SecurityAccountResponse>;

  readonly orgId: string;

  /**
   * The org's people. `level` decides whether known people — the ones with no
   * record of their own — are in the answer at all; it defaults to `tracked`
   * server-side, so a caller that says nothing gets the list as it always was.
   */
  listSubjects(params?: SubjectListParams): Promise<SubjectListResponse>;
  createSubject(body: CreateSubjectBody): Promise<CreateSubjectResponse>;
  getRecord(subjectId: string): Promise<SubjectRecord>;
  /**
   * What this person is to the org now. Answers the record's `subject` block as
   * it now stands; the same status again is a 200 no-op, not an error.
   */
  setSubjectStatus(
    subjectId: string,
    body: SetSubjectStatusBody,
  ): Promise<SetSubjectStatusResponse>;
  /**
   * Confirm, set aside or remove one of a person's accounts. Owner-only; answers
   * the record's `profile_links` block as it now stands.
   */
  profileLink(
    subjectId: string,
    anchorId: string,
    action: ProfileLinkAction,
  ): Promise<ProfileLinkResponse>;
  /**
   * `auto` sends no query and lets the server pick (§3e): `current` with no run
   * live, `stable` — pinned to the dossier as it stood before the live run —
   * with one. `draft` is the reader asking for the live run's writes anyway.
   */
  getBrief(subjectId: string, view: BriefViewRequest): Promise<SubjectBrief>;
  /** The Data tab's read — the substrate behind the brief (proposed contract). */
  getSubjectData(subjectId: string): Promise<SubjectData>;
  /** The Network tab's read: one hop off the subject plus that hop's closure. */
  getNetwork(subjectId: string): Promise<NetworkResponse>;

  /* ————————————————————————— the knowledge base ————————————————————————— */

  /**
   * The search over everything the org holds. An absent or too-short `q` is not
   * an error and not a request for everything: what comes back is the ten most
   * connected of each kind, which is the page as it opens.
   */
  searchKnowledge(params?: KnowledgeSearchParams): Promise<KnowledgeSearchResponse>;
  /** One of them, in full: who it touches, its graph, its facts, its provenance. */
  getKnowledge(entityId: string, accountId?: string): Promise<KnowledgePanel>;
  /**
   * Every tie the org holds, for the footer's whole-graph view. A security org
   * must name the account; every other org must not.
   */
  getKnowledgeGraph(accountId?: string): Promise<KnowledgeGraph>;
  /**
   * **The page's one write** (organisation-profile note §1.1): one
   * `org_relation` fact between the record in the path and the one in the body.
   *
   * `entityId` is the record whose page the office is standing on, and the
   * body's `direction` says which of the fact's two slots it takes — which is
   * why there is no second id here: one of the pair is never the client's to
   * choose. The same link sent twice is the same fact, and the response says so.
   */
  linkKnowledge(entityId: string, body: KnowledgeLinkBody): Promise<KnowledgeLinkResponse>;
  /** A family tie between two people: `relation` is what the other is to this one. */
  linkFamily(entityId: string, body: KnowledgeFamilyLinkBody): Promise<KnowledgeLinkResponse>;
  /**
   * **What the record is called** (organisations-as-subjects note §2 chunk 1) —
   * the second write on this surface, and the first way to change a name without
   * folding two records into one.
   *
   * One endpoint for all three doors, because the knowledge base is the
   * every-kind surface: the panel's Rename for an organisation or an event, and
   * the person record's own header. The same name sent twice changes nothing and
   * the response says so in `unchanged`.
   */
  renameKnowledge(entityId: string, body: KnowledgeRenameBody): Promise<KnowledgeRenameResponse>;
  /**
   * **Two records that are one** (organisations-as-subjects note §2 chunk 1,
   * "Join by hand") — the third write on this surface, and the first fold the
   * office can do without an operator at a terminal.
   *
   * `entityId` is the record whose entry the office is standing on and the
   * body's `keep` says which of the two survives — `'this'` for the one in the
   * path, `'other'` for the one in the body — which is why the id and the name
   * are never chosen separately: the record that survives keeps its own name.
   *
   * People only. There is no repeat: the second press finds the record it named
   * is now a redirect and answers 404.
   */
  joinKnowledge(entityId: string, body: KnowledgeJoinBody): Promise<KnowledgeJoinResponse>;
  /**
   * **What the join would do, before it is done** (fold-tracked-people note §7)
   * — the same server-owned sentence the merge card carries and the connector
   * reads back, for the direction `keep` names.
   *
   * A **GET**, and a read: it writes nothing, and the pair is already on the
   * page. `keep` is in the query for the reason it is in the body — the sentence
   * is direction-specific, which is the whole reason it hangs on this door — so
   * the two directions are two reads and neither can be shown in place of the
   * other.
   *
   * `readback` is null when either record has gone since the page loaded. That
   * is not an error and does not stop the join: the preview is informative and
   * the door is still the door.
   */
  getJoinPreview(
    entityId: string,
    params: { other: string; keep: KnowledgeJoinKeep },
  ): Promise<KnowledgeJoinPreview>;

  /* ————————————————————————— organisations ————————————————————————— */

  /**
   * **The office's own roster of organisations** (organisations-as-subjects note
   * §1 R8) — tracked only, name-searchable, keyset-paged.
   *
   * A sibling surface rather than `/subjects` with a kind, for the reason the
   * note's "As grounded" 5 gives: an organisation has no status, no identity
   * block and no held-claim tiers, and every one of those would have arrived on
   * a shared response as a key a reader has to know to ignore.
   */
  listOrganisations(params?: OrganisationListParams): Promise<OrganisationListResponse>;
  /**
   * **New Organisation** — answers **the record it just made**, exactly as the
   * person create answers with the seeded record.
   *
   * `entity_id` and `force_new` are the two answers to `NAME_CONFLICT` and mean
   * what they mean on the person door: the same organisation already on file as
   * an untracked node (adopt it), or a different one that shares the name.
   */
  createOrganisation(body: CreateOrganisationBody): Promise<CreateOrganisationResponse>;
  /** The record page's read. 404 for anything that is not a tracked organisation. */
  getOrganisation(entityId: string): Promise<OrganisationRecord>;
  /**
   * **Stop tracking.** Drops the record row and nothing else — the organisation
   * stays as knowledge, on the graph and on every record that names it. This is
   * not the person shred.
   */
  untrackOrganisation(entityId: string): Promise<UntrackOrganisationResponse>;
  /**
   * **Add what you know**: the website, what the office knows, or both. The same
   * three effects the create door's own two fields have, through the same path.
   */
  addOrganisationStatement(
    entityId: string,
    body: OrganisationStatementBody,
  ): Promise<OrganisationStatementResponse>;

  createInput(subjectId: string, body: CreateInputBody): Promise<InputExtractionResult>;
  extractRetry(inputId: string): Promise<ExtractRetryResponse>;
  editInput(inputId: string, body: EditInputBody): Promise<InputExtractionResult>;
  removeInput(inputId: string): Promise<RemoveInputResponse>;
  reclassifyInteraction(interactionId: string): Promise<ReclassifyInteractionResponse>;

  createRun(subjectId: string, body: CreateRunBody): Promise<CreateRunResponse>;
  getRun(runId: string): Promise<RunDetail>;
  listRuns(subjectId: string): Promise<RunListResponse>;
  /** What each later Re-run and Refresh found (dossier Re-run and Refresh spec §3.5). */
  listRunFindings(subjectId: string): Promise<RunFindingsResponse>;
  resumeRun(runId: string, body?: ResumeRunBody): Promise<ResumeRunResponse>;
  /** Stop a run the office started — beside `resumeRun`, the office's two acts on a run. */
  cancelRun(runId: string): Promise<CancelRunResponse>;

  getNeedsAction(): Promise<NeedsActionResponse>;
  /** The org's own two settings. A key comes back `null` when the org never set it. */
  getOrgSettings(): Promise<OrgSettingsView>;
  /** Balance, this month's quick looks and recent charges; `charging_enabled: false` when not enrolled. */
  getCredits(): Promise<CreditSummary>;
  /** The three strings a member pastes into claude.ai to add the connector. */
  getConnector(): Promise<ConnectorView>;
  /** What the office's Resume of a failed run costs, on an enrolled account. */
  getRunCreditPrice(runId: string): Promise<RunCreditPrice>;
  getEventScanCreditPrice(eventId: string): Promise<EventScanCreditPrice>;
  getImportCreditPrice(importId: string): Promise<ImportCreditPrice>;
  /** Only the keys present are written; the rest of the settings bag is left alone. */
  updateOrgSettings(body: OrgSettingsUpdate): Promise<OrgSettingsView>;
  getDecision(decisionId: string): Promise<DecisionDetail>;
  answerDecision(decisionId: string, body: AnswerDecisionBody): Promise<AnswerDecisionResponse>;
  /** Separate two records a merge joined — the "Merged automatically" Undo on a merge. */
  unmerge(mergeDecisionId: string): Promise<UnmergeResult>;
  /** Take back a "different" merge triage recorded without asking, and ask again. */
  reopenTriage(decisionId: string): Promise<MergeTriageUndoResponse>;
  listDecisions(subjectId: string): Promise<DecisionLogResponse>;

  getFactClaims(lineageId: string): Promise<FactClaimsResponse>;
  correctFact(lineageId: string, body: CorrectFactBody): Promise<CorrectFactResponse>;

  /** The identity line's one destructive gesture; the dialog has already asked. */
  wrongPerson(subjectId: string): Promise<WrongPersonResponse>;
  /** The tombstone and the shred. Nothing to configure, so nothing to send. */
  deleteSubject(subjectId: string): Promise<DeleteSubjectResponse>;

  /* ————————————————————————— imports ————————————————————————— */

  /** The ledger: one entry per session this org has opened (import note §2.4). */
  listImports(): Promise<ImportSessionListEntry[]>;
  /**
   * **The one multipart call in the client** — the file rides beside the
   * description rather than in a JSON body (import note §5). `FormData` sets its
   * own `content-type` with the boundary in it, so nothing here may set one:
   * a hand-written header would name a boundary the body does not use and the
   * server would read an empty form.
   */
  /** `eventId` makes it an attendee list for that event's graph. */
  createImport(
    file: File,
    description: string,
    eventId?: string,
    replaceList?: boolean,
  ): Promise<ImportSession>;
  /** Polled while the session is in progress; `phase` is what moves. */
  getImport(importId: string): Promise<ImportSession>;
  /** The People tab, filtered and keyset-paged. */
  listImportRows(importId: string, params?: ImportRowParams): Promise<ImportRowsResponse>;
  /** The opening turn — run once, after the file is up. */
  openImport(importId: string): Promise<ImportTurnResponse>;
  /** One turn of the chat: what the office said, and what the planner answered. */
  sendImportMessage(importId: string, body: SendImportMessageBody): Promise<ImportTurnResponse>;
  /** The table editor's version of the plan — no model in it. */
  putImportPlan(importId: string, plan: ImportPlan): Promise<ImportPlanVersion>;
  /** Replace the file, before the session has committed. Multipart, as create is. */
  replaceImportFile(importId: string, file: File): Promise<ImportSession>;
  /** The dry run. Idempotent, and re-run after every edit. */
  previewImport(importId: string): Promise<ImportSession>;
  /** The per-row gesture: a level, a status, a name fixed inline, a namesake picked. */
  setImportRow(importId: string, locator: string, body: SetRowBody): Promise<ImportRow>;
  /** The same gesture across a filter — level and status only. */
  bulkImportRows(importId: string, body: BulkRowsBody): Promise<BulkRowsResponse>;
  /** Nothing written; the session closes and the file stays on it. */
  cancelImport(importId: string): Promise<ImportSession>;
  commitImport(importId: string, body: CommitImportBody): Promise<ImportSession>;
  /** The notes the office turned down at commit, turned back on. */
  readImportNotes(importId: string, body: ReadImportNotesBody): Promise<ImportSession>;
  /** The rows that failed for a reason somebody has since put right. */
  retryImportRows(importId: string): Promise<RetryImportRowsResponse>;
  /** Stop and report: what it removed, and who it kept and why. */
  unwindImport(importId: string): Promise<UnwindImportResponse>;
  /**
   * **The same file again, in a session that is open** (import note §4.2).
   *
   * Answers with the **new** session, not the one that was asked — which is what
   * makes it a navigation and not a refresh. Nothing is uploaded: the new
   * session points at the bytes the old one stored.
   */
  retryImport(importId: string): Promise<ImportSession>;

  /**
   * **Reports** (docs/notes/2026-09-22-reports-design.md). The writes that start
   * a run answer with its id; the others answer with the report as it now
   * stands.
   */
  /** **Events** (docs/notes/2026-09-23-event-graph-design.md §3a). */
  listEvents(params?: EventListParams): Promise<Events.EventListResponse>;
  createEvent(body: Events.CreateEventBody): Promise<Events.CreateEventResponse>;
  getEvent(entityId: string): Promise<Events.EventRecord>;
  untrackEvent(entityId: string): Promise<Events.UntrackEventResponse>;
  startEventScan(
    entityId: string,
    body: Events.StartEventScanBody,
  ): Promise<Events.StartEventScanResponse>;
  getEventAttendee(entityId: string, nodeId: string): Promise<Events.EventAttendeeDetail>;
  promoteAttendee(
    entityId: string,
    nodeId: string,
    body: Events.PromoteAttendeeBody,
  ): Promise<Events.PromoteAttendeeResponse>;
  dismissAttendee(entityId: string, nodeId: string): Promise<Events.DismissAttendeeResponse>;
  writeAnalysisReport(entityId: string, analysis: string): Promise<Reports.ReportRunStarted>;
  pickAttendeeReading(
    entityId: string,
    nodeId: string,
    body: Events.PickAttendeeReadingBody,
  ): Promise<Events.PickAttendeeReadingResponse>;
  unpickAttendeeReading(
    entityId: string,
    nodeId: string,
  ): Promise<Events.PickAttendeeReadingResponse>;
  addEventPeople(
    entityId: string,
    body: Events.AddEventPeopleBody,
  ): Promise<Events.AddEventPeopleResponse>;
  getEventPeople(entityId: string): Promise<Events.EventPeopleResponse>;
  deleteEventAttendees(entityId: string): Promise<Events.DeleteEventAttendeesResponse>;
  getEventAttendees(
    entityId: string,
    params?: EventAttendeesParams & { cursor?: string },
  ): Promise<Events.EventAttendeesResponse>;
  listReports(params?: Reports.ReportListQuery): Promise<Reports.ReportList>;
  createReport(body: Reports.CreateReportBody): Promise<Reports.ReportRunStarted>;
  getReport(reportId: string): Promise<Reports.ReportDetail>;
  getReportVersion(reportId: string, number: number): Promise<Reports.ReportVersion>;
  /** The version as a PDF, as the report's export toggles leave it, with the server's file name. */
  exportReport(reportId: string, number: number): Promise<{ file: Blob; filename: string }>;
  reviseReport(reportId: string, body: Reports.ReviseReportBody): Promise<Reports.ReportRunStarted>;
  updateReportInputs(
    reportId: string,
    body: Reports.UpdateReportInputsBody,
  ): Promise<Reports.ReportRunStarted>;
  refreshReport(
    reportId: string,
    body: Reports.RefreshReportBody,
  ): Promise<Reports.ReportRunStarted>;
  editReport(reportId: string, body: Reports.EditReportBody): Promise<Reports.ReportDetail>;
  renameReport(reportId: string, body: Reports.RenameReportBody): Promise<Reports.ReportDetail>;
  setReportPreferences(
    reportId: string,
    body: Reports.ReportPreferencesBody,
  ): Promise<Reports.ReportDetail>;
  archiveReport(reportId: string): Promise<Reports.ReportDetail>;
}

export function createApi(fetchWithAuth: AuthedFetch, orgId: string): Api {
  const base = `/orgs/${encodeURIComponent(orgId)}`;
  const get = <T>(path: string) => request<T>(fetchWithAuth, `${base}${path}`);
  const post = <T>(path: string, body?: unknown) =>
    request<T>(fetchWithAuth, `${base}${path}`, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  const patch = <T>(path: string, body: unknown) =>
    request<T>(fetchWithAuth, `${base}${path}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  const put = <T>(path: string, body: unknown) =>
    request<T>(fetchWithAuth, `${base}${path}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  const del = <T>(path: string) =>
    request<T>(fetchWithAuth, `${base}${path}`, { method: 'DELETE' });

  /*
    The multipart post. `request` is deliberately not reused: it sets
    `content-type: application/json` on every call that carries a body, and a
    `FormData` body needs the browser's own header — `multipart/form-data` plus
    the boundary it generated. Naming the type by hand here would send a boundary
    nothing in the body matches, and multer would parse no fields and no file.
  */
  const upload = async <T>(path: string, form: FormData): Promise<T> => {
    const response = await fetchWithAuth(`${base}${path}`, {
      method: 'POST',
      body: form,
      headers: new Headers({ accept: 'application/json' }),
    });
    if (!response.ok) throw await toApiError(response);
    return (await response.json()) as T;
  };

  return {
    orgId,
    demoManifest: () => get('/demo/manifest'),
    demoEvidence: (id) => get(`/demo/evidence/${id}`),
    securityAccounts: (state = 'active', offset = 0, q = '') =>
      get<Security.SecurityAccountListResponse>(`/security/accounts${query({ state, offset, q })}`),
    createSecurityAccount: (body) =>
      post<Security.SecurityAccountResponse>('/security/accounts', body),
    securityAccount: (id) =>
      get<Security.SecurityAccountDetailResponse>(`/security/accounts/${id}`),
    updateSecurityAccount: (id, body) =>
      patch<Security.SecurityAccountResponse>(`/security/accounts/${id}`, body),

    listSubjects: (params = {}) =>
      get<SubjectListResponse>(
        `/subjects${query({
          q: params.q,
          cursor: params.cursor,
          limit: params.limit,
          status: params.status,
          research_status: params.research_status,
          sort: params.sort,
          level: params.level,
          import_id: params.import_id,
          account_id: params.account_id,
        })}`,
      ),
    createSubject: (body) => post<CreateSubjectResponse>('/subjects', body),
    getRecord: (subjectId) => get<SubjectRecord>(`/subjects/${subjectId}/record`),
    setSubjectStatus: (subjectId, body) =>
      post<SetSubjectStatusResponse>(`/subjects/${subjectId}/status`, body),
    profileLink: (subjectId, anchorId, action) =>
      post<ProfileLinkResponse>(`/subjects/${subjectId}/profile-links/${anchorId}`, { action }),
    /* `auto` must send the bare path, not `?view=`: the query schema is strict
       and the server's "you decide" signal is the parameter's absence. */
    getBrief: (subjectId, view) =>
      get<SubjectBrief>(
        `/subjects/${subjectId}/brief${query({ view: view === 'draft' ? 'draft' : undefined })}`,
      ),
    getSubjectData: (subjectId) => get<SubjectData>(`/subjects/${subjectId}/data`),
    getNetwork: (subjectId) => get<NetworkResponse>(`/subjects/${subjectId}/network`),

    searchKnowledge: (params = {}) =>
      get<KnowledgeSearchResponse>(
        `/knowledge${query({ q: params.q, kind: params.kind, account_id: params.account_id })}`,
      ),
    getKnowledge: (entityId, accountId) =>
      get<KnowledgePanel>(`/knowledge/${entityId}${query({ account_id: accountId })}`),
    getKnowledgeGraph: (accountId) =>
      get<KnowledgeGraph>(`/knowledge-graph${query({ account_id: accountId })}`),
    linkKnowledge: (entityId, body) =>
      post<KnowledgeLinkResponse>(`/knowledge/${entityId}/links`, body),
    linkFamily: (entityId, body) =>
      post<KnowledgeLinkResponse>(`/knowledge/${entityId}/family`, body),
    renameKnowledge: (entityId, body) =>
      post<KnowledgeRenameResponse>(`/knowledge/${entityId}/rename`, body),
    joinKnowledge: (entityId, body) =>
      post<KnowledgeJoinResponse>(`/knowledge/${entityId}/join`, body),
    getJoinPreview: (entityId, params) =>
      get<KnowledgeJoinPreview>(
        `/knowledge/${entityId}/join-preview${query({ other: params.other, keep: params.keep })}`,
      ),

    listOrganisations: (params = {}) =>
      get<OrganisationListResponse>(
        `/organisations${query({
          q: params.q,
          cursor: params.cursor,
          limit: params.limit,
          account_id: params.account_id,
        })}`,
      ),
    createOrganisation: (body) => post<CreateOrganisationResponse>('/organisations', body),
    getOrganisation: (entityId) => get<OrganisationRecord>(`/organisations/${entityId}`),
    untrackOrganisation: (entityId) =>
      del<UntrackOrganisationResponse>(`/organisations/${entityId}`),
    addOrganisationStatement: (entityId, body) =>
      post<OrganisationStatementResponse>(`/organisations/${entityId}/statements`, body),

    createInput: (subjectId, body) =>
      post<InputExtractionResult>(`/subjects/${subjectId}/inputs`, body),
    extractRetry: (inputId) => post<ExtractRetryResponse>(`/inputs/${inputId}/extract-retry`),
    editInput: (inputId, body) => patch<InputExtractionResult>(`/inputs/${inputId}`, body),
    removeInput: (inputId) => del<RemoveInputResponse>(`/inputs/${inputId}`),
    reclassifyInteraction: (interactionId) =>
      post<ReclassifyInteractionResponse>(`/interactions/${interactionId}/reclassify`),

    createRun: (subjectId, body) => post<CreateRunResponse>(`/subjects/${subjectId}/runs`, body),
    getRun: (runId) => get<RunDetail>(`/runs/${runId}`),
    listRuns: (subjectId) => get<RunListResponse>(`/subjects/${subjectId}/runs`),
    listRunFindings: (subjectId) =>
      get<RunFindingsResponse>(`/subjects/${subjectId}/runs/findings`),
    resumeRun: (runId, body) => post<ResumeRunResponse>(`/runs/${runId}/resume`, body),
    cancelRun: (runId) => post<CancelRunResponse>(`/runs/${runId}/cancel`),

    getNeedsAction: () => get<NeedsActionResponse>('/needs-action'),
    getOrgSettings: () => get<OrgSettingsView>('/settings'),
    getCredits: () => get<CreditSummary>('/credits'),
    getConnector: () => get<ConnectorView>('/mcp/connector'),
    getRunCreditPrice: (runId) => get<RunCreditPrice>(`/credits/runs/${runId}`),
    getEventScanCreditPrice: (eventId) =>
      get<EventScanCreditPrice>(`/credits/events/${eventId}/scan`),
    getImportCreditPrice: (importId) => get<ImportCreditPrice>(`/credits/imports/${importId}`),
    updateOrgSettings: (body) => patch<OrgSettingsView>('/settings', body),
    getDecision: (decisionId) => get<DecisionDetail>(`/decisions/${decisionId}`),
    answerDecision: (decisionId, body) =>
      post<AnswerDecisionResponse>(`/decisions/${decisionId}/answer`, body),
    unmerge: (mergeDecisionId) => post<UnmergeResult>(`/merges/${mergeDecisionId}/unmerge`),
    reopenTriage: (decisionId) => post<MergeTriageUndoResponse>(`/merge-triage/${decisionId}/undo`),
    listDecisions: (subjectId) => get<DecisionLogResponse>(`/subjects/${subjectId}/decisions`),

    getFactClaims: (lineageId) => get<FactClaimsResponse>(`/facts/${lineageId}/claims`),
    correctFact: (lineageId, body) =>
      post<CorrectFactResponse>(`/facts/${lineageId}/correct`, body),

    wrongPerson: (subjectId) =>
      post<WrongPersonResponse>(`/subjects/${subjectId}/identity/wrong-person`, { confirm: true }),

    deleteSubject: (subjectId) => del<DeleteSubjectResponse>(`/subjects/${subjectId}`),

    listImports: () => get<ImportSessionListEntry[]>('/imports'),
    createImport: (file, description, eventId, replaceList) => {
      const form = new FormData();
      form.append('file', file);
      form.append('description', description);
      if (eventId) form.append('event_id', eventId);
      if (eventId && replaceList) form.append('replace_list', 'true');
      return upload<ImportSession>('/imports', form);
    },
    getImport: (importId) => get<ImportSession>(`/imports/${importId}`),
    listImportRows: (importId, params = {}) =>
      get<ImportRowsResponse>(
        `/imports/${importId}/rows${query({
          bin: params.bin,
          bin_reason: params.bin_reason,
          /* What the dry run said, which is a different question from what the
             commit did — every row's `outcome` is `pending` until one has run. */
          preview_outcome: params.preview_outcome,
          outcome: params.outcome,
          level: params.level,
          status: params.status,
          q: params.q,
          /* A grouping and not a filter: it selects nothing, it only brings the
             copies of one repeated name up beside the earliest of them (§1.5). */
          group: params.group,
          cursor: params.cursor,
          limit: params.limit,
        })}`,
      ),
    openImport: (importId) => post<ImportTurnResponse>(`/imports/${importId}/open`),
    sendImportMessage: (importId, body) =>
      post<ImportTurnResponse>(`/imports/${importId}/messages`, body),
    putImportPlan: (importId, plan) => put<ImportPlanVersion>(`/imports/${importId}/plan`, plan),
    replaceImportFile: (importId, file) => {
      const form = new FormData();
      form.append('file', file);
      return upload<ImportSession>(`/imports/${importId}/file`, form);
    },
    previewImport: (importId) => post<ImportSession>(`/imports/${importId}/preview`),
    setImportRow: (importId, locator, body) =>
      post<ImportRow>(`/imports/${importId}/rows/${encodeURIComponent(locator)}`, body),
    bulkImportRows: (importId, body) =>
      post<BulkRowsResponse>(`/imports/${importId}/rows/bulk`, body),
    cancelImport: (importId) => post<ImportSession>(`/imports/${importId}/cancel`),
    commitImport: (importId, body) => post<ImportSession>(`/imports/${importId}/commit`, body),
    readImportNotes: (importId, body) =>
      post<ImportSession>(`/imports/${importId}/notes/read`, body),
    retryImportRows: (importId) => post<RetryImportRowsResponse>(`/imports/${importId}/rows/retry`),
    unwindImport: (importId) => post<UnwindImportResponse>(`/imports/${importId}/unwind`),
    retryImport: (importId) => post<ImportSession>(`/imports/${importId}/retry`),

    listEvents: (params = {}) =>
      get<Events.EventListResponse>(
        `/events${query({
          q: params.q,
          cursor: params.cursor,
          limit: params.limit,
          account_id: params.account_id,
        })}`,
      ),
    createEvent: (body) => post<Events.CreateEventResponse>('/events', body),
    getEvent: (entityId) => get<Events.EventRecord>(`/events/${entityId}`),
    untrackEvent: (entityId) => del<Events.UntrackEventResponse>(`/events/${entityId}`),
    startEventScan: (entityId, body) =>
      post<Events.StartEventScanResponse>(`/events/${entityId}/scan`, body),
    getEventAttendee: (entityId, nodeId) =>
      get<Events.EventAttendeeDetail>(`/events/${entityId}/attendees/${nodeId}`),
    promoteAttendee: (entityId, nodeId, body) =>
      post<Events.PromoteAttendeeResponse>(`/events/${entityId}/attendees/${nodeId}/promote`, body),
    deleteEventAttendees: (entityId) =>
      del<Events.DeleteEventAttendeesResponse>(`/events/${entityId}/attendees`),
    dismissAttendee: (entityId, nodeId) =>
      post<Events.DismissAttendeeResponse>(`/events/${entityId}/attendees/${nodeId}/dismiss`),
    writeAnalysisReport: (entityId, analysis) =>
      post<Reports.ReportRunStarted>(
        `/events/${entityId}/analyses/${encodeURIComponent(analysis)}/report`,
      ),
    pickAttendeeReading: (entityId, nodeId, body) =>
      post<Events.PickAttendeeReadingResponse>(
        `/events/${entityId}/attendees/${nodeId}/pick`,
        body,
      ),
    unpickAttendeeReading: (entityId, nodeId) =>
      del<Events.PickAttendeeReadingResponse>(`/events/${entityId}/attendees/${nodeId}/pick`),
    addEventPeople: (entityId, body) =>
      post<Events.AddEventPeopleResponse>(`/events/${entityId}/attendees/people`, body),
    getEventPeople: (entityId) => get<Events.EventPeopleResponse>(`/events/${entityId}/people`),
    getEventAttendees: (entityId, params = {}) =>
      get<Events.EventAttendeesResponse>(
        `/events/${entityId}/attendees${query({
          q: params.q,
          status: params.status,
          review: params.review,
          frame: params.frame,
          tier: params.tier,
          cursor: params.cursor,
        })}`,
      ),
    listReports: (params = {}) => get<Reports.ReportList>(`/reports${query({ ...params })}`),
    createReport: (body) => post<Reports.ReportRunStarted>('/reports', body),
    getReport: (reportId) => get<Reports.ReportDetail>(`/reports/${reportId}`),
    getReportVersion: (reportId, number) =>
      get<Reports.ReportVersion>(`/reports/${reportId}/versions/${number}`),
    exportReport: async (reportId, number) => {
      const response = await fetchWithAuth(
        `${base}/reports/${reportId}/export${query({ version: number })}`,
        {
          headers: new Headers({ accept: 'application/pdf' }),
        },
      );
      if (!response.ok) throw await toApiError(response);
      return {
        file: await response.blob(),
        filename: dispositionFilename(response.headers.get('content-disposition')) ?? 'Report.pdf',
      };
    },
    reviseReport: (reportId, body) =>
      post<Reports.ReportRunStarted>(`/reports/${reportId}/revise`, body),
    updateReportInputs: (reportId, body) =>
      post<Reports.ReportRunStarted>(`/reports/${reportId}/inputs`, body),
    refreshReport: (reportId, body) =>
      post<Reports.ReportRunStarted>(`/reports/${reportId}/refresh`, body),
    editReport: (reportId, body) => post<Reports.ReportDetail>(`/reports/${reportId}/edits`, body),
    renameReport: (reportId, body) =>
      patch<Reports.ReportDetail>(`/reports/${reportId}/title`, body),
    setReportPreferences: (reportId, body) =>
      patch<Reports.ReportDetail>(`/reports/${reportId}/preferences`, body),
    archiveReport: (reportId) => post<Reports.ReportDetail>(`/reports/${reportId}/archive`),
  };
}

/**
 * **The server route each write method sends to**, named by the signature the
 * server's own route table gives it (`OFFICE_WRITE_ROUTES`, generated from the
 * handlers' `@Writes`). A method's axis is looked up through this and never
 * written here, so when the server widens or tightens a route the write-side
 * gates move with the regenerated table.
 *
 * `write-contract.spec.ts` holds this to the requests the methods actually
 * send: a write method missing here, or naming a route other than the one its
 * path resolves to, fails it.
 */
export const WRITE_ROUTES = {
  createSecurityAccount: 'POST /orgs/:orgId/security/accounts',
  updateSecurityAccount: 'PATCH /orgs/:orgId/security/accounts/:accountId',
  createSubject: 'POST /orgs/:orgId/subjects',
  setSubjectStatus: 'POST /orgs/:orgId/subjects/:id/status',
  profileLink: 'POST /orgs/:orgId/subjects/:id/profile-links/:anchorId',
  linkKnowledge: 'POST /orgs/:orgId/knowledge/:entityId/links',
  linkFamily: 'POST /orgs/:orgId/knowledge/:entityId/family',
  renameKnowledge: 'POST /orgs/:orgId/knowledge/:entityId/rename',
  joinKnowledge: 'POST /orgs/:orgId/knowledge/:entityId/join',
  createEvent: 'POST /orgs/:orgId/events',
  untrackEvent: 'DELETE /orgs/:orgId/events/:id',
  startEventScan: 'POST /orgs/:orgId/events/:id/scan',
  promoteAttendee: 'POST /orgs/:orgId/events/:id/attendees/:nodeId/promote',
  dismissAttendee: 'POST /orgs/:orgId/events/:id/attendees/:nodeId/dismiss',
  writeAnalysisReport: 'POST /orgs/:orgId/events/:id/analyses/:analysis/report',
  pickAttendeeReading: 'POST /orgs/:orgId/events/:id/attendees/:nodeId/pick',
  unpickAttendeeReading: 'DELETE /orgs/:orgId/events/:id/attendees/:nodeId/pick',
  addEventPeople: 'POST /orgs/:orgId/events/:id/attendees/people',
  deleteEventAttendees: 'DELETE /orgs/:orgId/events/:id/attendees',
  createOrganisation: 'POST /orgs/:orgId/organisations',
  untrackOrganisation: 'DELETE /orgs/:orgId/organisations/:id',
  addOrganisationStatement: 'POST /orgs/:orgId/organisations/:id/statements',
  createInput: 'POST /orgs/:orgId/subjects/:id/inputs',
  extractRetry: 'POST /orgs/:orgId/inputs/:id/extract-retry',
  editInput: 'PATCH /orgs/:orgId/inputs/:id',
  removeInput: 'DELETE /orgs/:orgId/inputs/:id',
  reclassifyInteraction: 'POST /orgs/:orgId/interactions/:id/reclassify',
  createRun: 'POST /orgs/:orgId/subjects/:id/runs',
  resumeRun: 'POST /orgs/:orgId/runs/:id/resume',
  cancelRun: 'POST /orgs/:orgId/runs/:id/cancel',
  updateOrgSettings: 'PATCH /orgs/:orgId/settings',
  answerDecision: 'POST /orgs/:orgId/decisions/:id/answer',
  unmerge: 'POST /orgs/:orgId/merges/:decisionId/unmerge',
  reopenTriage: 'POST /orgs/:orgId/merge-triage/:decisionId/undo',
  correctFact: 'POST /orgs/:orgId/facts/:lineageId/correct',
  wrongPerson: 'POST /orgs/:orgId/subjects/:id/identity/wrong-person',
  deleteSubject: 'DELETE /orgs/:orgId/subjects/:id',
  createImport: 'POST /orgs/:orgId/imports',
  openImport: 'POST /orgs/:orgId/imports/:id/open',
  sendImportMessage: 'POST /orgs/:orgId/imports/:id/messages',
  putImportPlan: 'PUT /orgs/:orgId/imports/:id/plan',
  replaceImportFile: 'POST /orgs/:orgId/imports/:id/file',
  previewImport: 'POST /orgs/:orgId/imports/:id/preview',
  setImportRow: 'POST /orgs/:orgId/imports/:id/rows/:locator',
  bulkImportRows: 'POST /orgs/:orgId/imports/:id/rows/bulk',
  cancelImport: 'POST /orgs/:orgId/imports/:id/cancel',
  commitImport: 'POST /orgs/:orgId/imports/:id/commit',
  readImportNotes: 'POST /orgs/:orgId/imports/:id/notes/read',
  retryImportRows: 'POST /orgs/:orgId/imports/:id/rows/retry',
  unwindImport: 'POST /orgs/:orgId/imports/:id/unwind',
  retryImport: 'POST /orgs/:orgId/imports/:id/retry',
  createReport: 'POST /orgs/:orgId/reports',
  reviseReport: 'POST /orgs/:orgId/reports/:id/revise',
  updateReportInputs: 'POST /orgs/:orgId/reports/:id/inputs',
  refreshReport: 'POST /orgs/:orgId/reports/:id/refresh',
  editReport: 'POST /orgs/:orgId/reports/:id/edits',
  renameReport: 'PATCH /orgs/:orgId/reports/:id/title',
  setReportPreferences: 'PATCH /orgs/:orgId/reports/:id/preferences',
  archiveReport: 'POST /orgs/:orgId/reports/:id/archive',
} as const satisfies Partial<Record<keyof Api, OfficeWriteRoute>>;

export type WriteMethod = keyof typeof WRITE_ROUTES;

/** The least a reader must be able to write for this method's request to be accepted. */
export const axisOfWrite = (method: WriteMethod): WriteAxis =>
  OFFICE_WRITE_ROUTES[WRITE_ROUTES[method]];
